
import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseTypewriterOptions {
  fullText: string;
  enabled: boolean;
  speed?: number;
}

export interface UseTypewriterResult {
  displayedText: string;
  isTyping: boolean;
  skip: () => void;
}

const CODE_FENCE = '```';
const CHUNK_DELIMITERS = new Set([
  '。',
  '！',
  '？',
  '、',
  '.',
  '!',
  '?',
  ',',
  ';',
  ':',
  ' ',
  '\n',
]);

const MAX_CHUNK_LEN = 80;

const MIN_TICK_INTERVAL_MS = 30;
const MAX_TICK_INTERVAL_MS = 50;

export const splitIntoChunks = (text: string): string[] => {
  const chunks: string[] = [];
  let buffer = '';
  let i = 0;

  while (i < text.length) {
    if (text.startsWith(CODE_FENCE, i)) {
      if (buffer.length > 0) {
        chunks.push(buffer);
        buffer = '';
      }
      const closeIdx = text.indexOf(CODE_FENCE, i + CODE_FENCE.length);
      if (closeIdx === -1) {
        chunks.push(text.slice(i));
        i = text.length;
      } else {
        const end = closeIdx + CODE_FENCE.length;
        chunks.push(text.slice(i, end));
        i = end;
      }
      continue;
    }

    const ch = text[i];
    buffer += ch;

    if (CHUNK_DELIMITERS.has(ch)) {
      chunks.push(buffer);
      buffer = '';
    } else if (buffer.length >= MAX_CHUNK_LEN) {
      chunks.push(buffer);
      buffer = '';
    }

    i += 1;
  }

  if (buffer.length > 0) {
    chunks.push(buffer);
  }

  return chunks;
};

export const useTypewriter = ({
  fullText,
  enabled,
  speed = 30,
}: UseTypewriterOptions): UseTypewriterResult => {
  const [displayedText, setDisplayedText] = useState<string>(() => (enabled ? '' : fullText));
  const [isTyping, setIsTyping] = useState<boolean>(() => enabled && fullText.length > 0);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fullTextRef = useRef<string>(fullText);

  useEffect(() => {
    fullTextRef.current = fullText;
  }, [fullText]);

  useEffect(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!enabled || fullText.length === 0) {
      setDisplayedText(fullText);
      setIsTyping(false);
      return;
    }

    const chunks = splitIntoChunks(fullText);

    if (chunks.length === 0) {
      setDisplayedText(fullText);
      setIsTyping(false);
      return;
    }

    const safeSpeed = Number.isFinite(speed) && speed > 0 ? speed : 30;
    const tickIntervalMs = Math.min(
      MAX_TICK_INTERVAL_MS,
      Math.max(MIN_TICK_INTERVAL_MS, Math.round(1000 / safeSpeed))
    );

    let chunkIndex = 0;
    const firstChunk = chunks[chunkIndex];
    chunkIndex += 1;
    setDisplayedText(firstChunk);
    setIsTyping(chunkIndex < chunks.length);

    if (chunkIndex >= chunks.length) {
      return;
    }

    intervalRef.current = setInterval(() => {
      if (chunkIndex >= chunks.length) {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsTyping(false);
        return;
      }

      const nextChunk = chunks[chunkIndex];
      chunkIndex += 1;
      setDisplayedText((prev) => prev + nextChunk);

      if (chunkIndex >= chunks.length) {
        if (intervalRef.current !== null) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        setIsTyping(false);
      }
    }, tickIntervalMs);

    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [fullText, enabled, speed]);

  const skip = useCallback(() => {
    if (intervalRef.current !== null) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setDisplayedText(fullTextRef.current);
    setIsTyping(false);
  }, []);

  return { displayedText, isTyping, skip };
};
